<template>
  <loading-page
    v-if="loading"
    full
  />
  <validation-observer
    v-else
    v-slot="{ invalid }"
    ref="users"
    tag="div"
    class="h-100"
  >
    <form-wrapper
      full-width
      full-height
    >
      <template v-slot:body>
        <paper-simple class="h-100 m-auto">
          <div class="more-visible">
            <p>
              A role contains a set of permissions that allows you to perform specific actions on Captain resources.
              To make permissions available to users, you grant roles to the members.
            </p>
            <ul>
              <li class="mt-1">
                <span class="text-thicker">
                  Owner:
                </span>
                The owner of the project with all editor permissions and permissions for the following actions:
                Manage roles and permissions for a project and all resources within the project.
                Set up billing for a project.
              </li>
              <li class="mt-1">
                <span class="text-thicker">
                  Editor:
                </span>
                All viewer permissions, plus permissions for actions that modify state, such as changing existing resources.
              </li>
              <li class="mt-1">
                <span class="text-thicker">
                  Viewer:
                </span>
                Permissions for read-only actions that do not affect the state, such as viewing (but not modifying) existing resources or data.
              </li>
            </ul>

            <div class="users">
              <div class="mt-2 col-12 col-lg-8 m-auto">
                <div
                  v-if="editing"
                  class="d-flex"
                >
                  <div class="w-50 icon-left">
                    <label>
                      Email
                    </label>
                    <b-form-input
                      :value="user.email"
                      disabled
                    />
                  </div>
                  <div class="w-50">
                    <label>
                      Role
                    </label>
                    <validation-provider
                      #default="{ errors }"
                      name="Role"
                      rules="required"
                    >
                      <b-form-select
                        v-model="roleId"
                        :options="roleOptions"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </div>
                </div>
                <template v-else>
                  <div class="d-flex no-icon">
                    <div class="w-50">
                      <label>
                        Email
                      </label>
                    </div>
                    <div class="w-50">
                      <label>
                        Role
                      </label>
                    </div>
                  </div>
                  <template v-for="email in emails">
                    <div
                      :key="email.index"
                      class="d-flex mt-1 position-relative"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Email"
                        :vid="`email${email.index}`"
                        rules="required|email"
                        class="w-50 icon-left"
                      >
                        <b-input
                          :id="`email${email.index}`"
                          v-model="email.email"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Enter user’s email..."
                          class="mb-0"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                      <validation-provider
                        #default="{ errors }"
                        name="Role"
                        :vid="`role${email.index}`"
                        rules="required"
                        class="w-50"
                      >
                        <b-form-select
                          :id="`role${email.index}`"
                          v-model="email.role"
                          :state="errors.length > 0 ? false : null"
                          class="mb-0 mr-0"
                          :options="roleOptions"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                      <a
                        class="icon-right pt-10"
                        @click="onRemoveField(email.index)"
                      >
                        <GjIcon name="Delete" />
                      </a>
                    </div>
                  </template>
                  <div class="px-1 px-md-2 mt-1">
                    <a
                      class="text-primary text-medium"
                      @click="onAddField"
                    >
                      + Add user
                    </a>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <span class="bg-icon">
            <GjIcon
              name="AvatarGroup"
              size="140"
              class="img-fluid"
            />
          </span>
        </paper-simple>
      </template>
      <template v-slot:footer>
        <b-button
          variant="bg-white"
          @click="onCancel()"
        >
          Cancel
        </b-button>
        <button-loading
          variant="bg-primary"
          class="ml-1"
          :spinning="invitingUser"
          :disabled="invalid"
          @click="onSave()"
        >Save</button-loading>
      </template>
    </form-wrapper>
  </validation-observer>
</template>
<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { email, required } from '@validations'
import redirectError from '@/services/redirectError'

export default {
  components: { ValidationProvider, ValidationObserver },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      required,
      email,
      roleId: null,
      emails: [
        {
          index: 0,
          id: 0,
          email: '',
          role: null,
        },
      ],
    }
  },
  computed: {
    loading() {
      return this.gettingUser || this.fetchingRoles
    },
    gettingUser() {
      return this.$store.state.projects.gettingUser
    },
    user() {
      return this.$store.state.projects.user
    },
    fetchingRoles() {
      return this.$store.state.roles.fetchingRoles
    },
    roles() {
      return this.$store.state.roles.roles
    },
    roleOptions() {
      return [
        ...(this.editing ? [] : [{ value: null, text: 'Select' }]),
        ...this.roles.map(role => ({
          value: role.id, text: role.name,
        })),
      ]
    },
    updatingUser() {
      return this.$store.state.projects.updatingUser
    },
    invitingUser() {
      return this.$store.state.projects.invitingUser
    },
    lastIndex() {
      return this.emails[this.emails.length - 1]?.index || 0
    },
    firstIndex() {
      return this.emails[0].index
    },
  },
  created() {
    this.$store
      .dispatch('roles/fetchRoles', {
        pageIndex: 0,
        pageSize: 500,
      })
      .catch(error => {
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching roles.')
      })

    if (this.editing) {
      this.$store.dispatch('projects/getUser', this.id)
        .then(() => {
          this.roleId = this.user.role.id
        })
        .catch(err => redirectError(err, this.$router, this.$route.path))
    }
  },
  methods: {
    onSave() {
      this.$emit('save', this.editing ? this.roleId : this.emails)
    },
    onCancel() {
      this.$router.push({
        name: 'project-settings',
        query: { tab: '1' },
      })
    },
    onAddField() {
      this.emails.push({
        index: this.lastIndex + 1,
        email: '',
        role: null,
      })
    },
    onRemoveField(index) {
      this.emails = this.emails.filter(obj => obj.index !== index)

      for (let i = 0; i < this.emails.length; i++) {
        this.emails[i].index = i
      }
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep {
  .paper {
    @media (min-width: 1200px) {
      width: 75%;
    }
  }
}
.users {
  border-top: 1px solid #e0e5eb;
  padding-top: 24px;
  .no-icon {
    margin-right: 34px;
  }
}
.pt-10 {
  padding-top: 10px;
}
</style>
